import {useCallback, useRef} from 'react';
import ReactQuill from 'react-quill';
import {TextTemplateItem} from '../types/templateStructure';

interface UseWhatsAppEditorActionsParams {
    events: {
        onSave?: (payload: TextTemplateItem[] | string) => void;
    };
    segmentedValue: string;
    isCharCountExceeded: boolean;
    prepareForSave: () => TextTemplateItem[];
    clearEditing: () => void;
    freeMessage: string;
    clearAllContent: () => void;
    onFreeMessageChange: (next: string) => void;
}

export function useWhatsAppEditorActions({
    events,
    segmentedValue,
    isCharCountExceeded,
    prepareForSave,
    clearEditing,
    freeMessage,
    clearAllContent,
    onFreeMessageChange,
}: UseWhatsAppEditorActionsParams) {
    const freeQuillRef = useRef<ReactQuill | null>(null);

    const handleFreeQuillFormat = (command: 'bold' | 'italic') => {
        if(command !== 'bold' && command !== 'italic') {
            return;
        }
        const quill = freeQuillRef.current?.getEditor();
        if(!quill) {
            return;
        }
        const range = quill.getSelection(true);
        if(!range) {
            quill.focus();
            return;
        }
        const currentFormat = quill.getFormat(range);
        const next = !currentFormat[command];
        if(range.length === 0) {
            quill.format(command, next, 'user');
        } else {
            quill.formatText(range.index, range.length, command, next, 'user');
        }
    };

    const handleSave = useCallback(() => {
        if(!events.onSave || isCharCountExceeded) return;
        if(segmentedValue === 'template') {
            events.onSave(prepareForSave());
        } else {
            events.onSave(freeMessage);
        }
        clearEditing();
    }, [segmentedValue, events.onSave, isCharCountExceeded, freeMessage, prepareForSave]);

    const handleClearAll = useCallback(() => {
        if(segmentedValue === 'template') {
            clearAllContent();
        } else {
            onFreeMessageChange('');
        }
    }, [segmentedValue]);

    return {
        freeQuillRef,
        handleFreeQuillFormat,
        handleSave,
        handleClearAll,
    };
}
